import React from 'react';
import Button from '../../../../components/Button';
import { CompetitorSearchModalProps } from './types';
import { getThreatRatingColor, getThreatRatingIcon } from './utils';
import { 
  formatBusinessTypes, 
  getBusinessTypeIcon, 
  formatRating 
} from '../../../../services/google-places';

const CompetitorSearchModal: React.FC<CompetitorSearchModalProps> = ({
  showSuggestions,
  setShowSuggestions,
  searchRadius,
  setSearchRadius,
  searchBusinessType,
  setSearchBusinessType,
  searchKeyword,
  setSearchKeyword,
  loadingSuggestions,
  suggestions,
  onSearch,
  onAddSuggestion
}) => {
  if (!showSuggestions) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#1e1e1e' }}>
          Find Nearby Competitors
        </h3>
        
        {/* Search Controls */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
              Search Radius (miles)
            </label>
            <input
              type="number"
              min="1"
              max="25"
              value={searchRadius}
              onChange={(e) => setSearchRadius(parseInt(e.target.value))}
              style={{ 
                width: '100%', 
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
              Business Type
            </label>
            <select
              value={searchBusinessType}
              onChange={(e) => setSearchBusinessType(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            >
              <option value="">All Types</option>
              <option value="restaurant">Restaurant</option>
              <option value="entertainment">Entertainment</option>
              <option value="bowling">Bowling</option>
              <option value="arcade">Arcade</option>
              <option value="gym">Gym</option>
              <option value="retail">Retail</option>
              <option value="bar">Bar</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
              Keyword (optional)
            </label>
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="e.g., 'family fun', 'arcade'"
              style={{ 
                width: '100%', 
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <Button 
            onClick={onSearch}
            disabled={loadingSuggestions}
            style={{ 
              backgroundColor: '#1890ff',
              color: 'white'
            }}
          >
            {loadingSuggestions ? 'Searching...' : 'Search'}
          </Button>
          <Button 
            onClick={() => setShowSuggestions(false)}
            style={{ 
              backgroundColor: 'white',
              color: '#1e1e1e',
              border: '1px solid #1e1e1e'
            }}
          >
            Close
          </Button>
        </div>

        {/* Suggestions Results */}
        {suggestions.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '1rem' }}>
              Found {suggestions.length} potential competitors within {searchRadius} miles
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: '1rem',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {suggestions.map((suggestion, index) => (
                <div key={index} style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '1rem',
                  backgroundColor: '#fff'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    marginBottom: '0.5rem' 
                  }}>
                    <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                      {getBusinessTypeIcon(suggestion.business_types)} {suggestion.name}
                    </h5>
                    <span style={{ 
                      fontSize: '0.8rem', 
                      color: getThreatRatingColor(suggestion.suggested_threat_rating),
                      fontWeight: 600
                    }}>
                      {getThreatRatingIcon(suggestion.suggested_threat_rating)} Threat: {suggestion.suggested_threat_rating}/5
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                    📍 {suggestion.formatted_address}
                  </div>
                  
                  <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    📏 {suggestion.distance_miles.toFixed(2)} miles away
                  </div>
                  
                  {suggestion.rating && (
                    <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      {formatRating(suggestion.rating, suggestion.user_ratings_total)}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    🏷️ {formatBusinessTypes(suggestion.business_types).join(', ')}
                  </div>
                  
                  <div style={{ fontSize: '0.9rem', marginBottom: '1rem', color: '#666' }}>
                    Type: {suggestion.suggested_competition_type}
                  </div>
                  
                  <Button 
                    onClick={() => onAddSuggestion(suggestion)}
                    style={{ 
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: '0.9rem'
                    }}
                  >
                    Add as Competitor
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorSearchModal;
